import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { LANE_H, STEPS_TOTAL, WR_TIME } from '@shared/gameConfig';

/**
 * Pace marker between the two lanes. A glowing medal climbs from the
 * bottom of the divider to the top over WR_TIME seconds, and the delta
 * (±x.xxs) floats next to it. Once the timer passes WR, ribbons turn red
 * and a vertical "WR PASSED" label flashes.
 */
export class WrTrack extends Container {
  private divider: Graphics;
  private medal: Container;
  private medalGlow: Graphics;
  private ticks: Graphics;
  private deltaText: Text;
  private wrPassedText: Text;
  private finishFlag: Text;

  private static readonly WIDTH = 26;

  constructor() {
    super();

    this.divider = new Graphics();
    this.divider
      .roundRect(0, 0, WrTrack.WIDTH, LANE_H, 4)
      .fill(0x1a1448)
      .stroke({ color: 0x444466, width: 1, alpha: 0.3 });
    this.addChild(this.divider);

    // Tick marks at every step (faint), finish line bold yellow
    this.ticks = new Graphics();
    for (let i = 0; i <= STEPS_TOTAL; i++) {
      const y = LANE_H - 2 - (i / STEPS_TOTAL) * (LANE_H - 10);
      const isFinish = i === STEPS_TOTAL;
      this.ticks.rect(2, y, WrTrack.WIDTH - 4, 1).fill({
        color: 0xf4c724,
        alpha: isFinish ? 1 : 0.12,
      });
    }
    this.addChild(this.ticks);

    // Medal sprite (drawn as graphics — same shape as the prototype's SVG)
    this.medal = new Container();
    this.medalGlow = new Graphics();
    this.medal.addChild(this.medalGlow);
    this.drawMedal(true);
    this.medal.x = WrTrack.WIDTH / 2;
    this.medal.y = LANE_H - 16;
    this.addChild(this.medal);

    this.finishFlag = new Text({
      text: '▲',
      style: new TextStyle({
        fontFamily: 'Bungee, Impact',
        fontSize: 7,
        fill: 0xf4c724,
        letterSpacing: 0.7,
        dropShadow: { color: 0xf4c724, alpha: 0.7, blur: 4, distance: 0 },
      }),
    });
    this.finishFlag.anchor.set(0.5, 0);
    this.finishFlag.x = WrTrack.WIDTH / 2;
    this.finishFlag.y = 4;
    this.addChild(this.finishFlag);

    this.deltaText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 8,
        fill: 0xf4c724,
      }),
    });
    this.deltaText.anchor.set(0, 0.5);
    this.deltaText.x = WrTrack.WIDTH + 4;
    this.addChild(this.deltaText);

    this.wrPassedText = new Text({
      text: 'WR PASSED',
      style: new TextStyle({
        fontFamily: 'Bungee, Impact',
        fontSize: 8,
        fill: 0xe63946,
        letterSpacing: 1.6,
      }),
    });
    this.wrPassedText.anchor.set(0.5);
    this.wrPassedText.x = WrTrack.WIDTH / 2;
    this.wrPassedText.y = 60;
    this.wrPassedText.rotation = -Math.PI / 2;
    this.wrPassedText.visible = false;
    this.addChild(this.wrPassedText);
  }

  private drawMedal(beating: boolean): void {
    const g = this.medalGlow;
    g.clear();

    const main = beating ? 0xf4c724 : 0xe63946;
    const trim = beating ? 0xa07c00 : 0x8a1a22;

    // Ribbon tails
    g.poly([-5, 4, -8, 18, -3, 14, -2, 6]).fill(0xe63946);
    g.poly([5, 4, 8, 18, 3, 14, 2, 6]).fill(0x3b82f6);

    // Outer glow halo
    g.circle(0, 0, 14).fill({ color: main, alpha: 0.2 });

    // Medal disc
    g.circle(0, 0, 11).fill(main).stroke({ color: 0xffffff, width: 1.5 });
    g.circle(0, 0, 8).stroke({ color: trim, width: 0.6, alpha: 0.6 });

    // "1" digit
    const t = new Text({
      text: '1',
      style: new TextStyle({
        fontFamily: 'Bungee, Impact',
        fontSize: 12,
        fill: beating ? 0x5a4a00 : 0x2a0608,
        fontWeight: '900',
      }),
    });
    t.anchor.set(0.5);
    // Clean prior digit text from past draws — track on the container
    const existing = this.medal.children.find((c) => (c as any)._medalDigit);
    if (existing) this.medal.removeChild(existing);
    (t as any)._medalDigit = true;
    this.medal.addChild(t);

    // Gloss
    g.ellipse(-3, -5, 5, 2.5).fill({ color: 0xffffff, alpha: 0.55 });
  }

  setTime(time: number, over: boolean): void {
    const pct = Math.min(1, time / WR_TIME);
    const bottom = pct * (LANE_H - 24);
    this.medal.y = LANE_H - 16 - bottom;

    const beating = !over && pct < 1;
    const passed = pct >= 1;

    this.drawMedal(beating);

    if (time > 0.4 && !over) {
      const sign = passed ? '+' : '−';
      this.deltaText.text = `${sign}${Math.abs(time - WR_TIME).toFixed(2)}s`;
      this.deltaText.style.fill = passed ? 0xe63946 : 0xf4c724;
      this.deltaText.y = this.medal.y;
      this.deltaText.visible = true;
    } else {
      this.deltaText.visible = false;
    }

    this.wrPassedText.visible = passed;
  }

  static getWidth(): number {
    return WrTrack.WIDTH;
  }
}
